import { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { usePageState, useAppStore } from '../../store/useAppStore';
import { Title } from '../common/Title';
import { useSound } from '../../hooks/audio/useSound';
import {
  DEFAULT_FLUID_SETTINGS,
  FLUID_PRESETS,
  InteractiveFluidBackground,
  type FluidSettings,
} from './InteractiveFluidBackground';

const panelContentVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.045,
      delayChildren: 0.06,
    },
  },
};

const panelItemVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const PRESET_TRANSITION_MS = 550;

const lerp = (start: number, end: number, t: number) => start + (end - start) * t;

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export const LoadingScreen: React.FC = () => {
  const { currentPage, loadingProgress } = usePageState();
  const setCurrentPage = useAppStore(state => state.setCurrentPage);
  const setSoundEnabled = useAppStore(state => state.setSoundEnabled);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [showEnterButton, setShowEnterButton] = useState(false);
  const [showFluidControls, setShowFluidControls] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<keyof typeof FLUID_PRESETS>('balanced');
  const [fluidSettings, setFluidSettings] = useState<FluidSettings>(DEFAULT_FLUID_SETTINGS);
  const [isPresetTransitioning, setIsPresetTransitioning] = useState(false);
  const [presetTransitionProgress, setPresetTransitionProgress] = useState(0);
  const presetAnimationRef = useRef<number | null>(null);
  const { init } = useSound();

  const isLoaded = loadingProgress >= 100;

  // Show enter button after 2 seconds at 100%
  useEffect(() => {
    if (isLoaded && !audioInitialized) {
      const timer = setTimeout(() => {
        setShowEnterButton(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, audioInitialized]);

  const handleEnterWithSound = useCallback(() => {
    setSoundEnabled(true);
    void init();
    setAudioInitialized(true);
    setCurrentPage('home');
  }, [init, setCurrentPage, setSoundEnabled]);

  const handleEnterWithoutSound = useCallback(() => {
    setSoundEnabled(false);
    setAudioInitialized(true);
    setCurrentPage('home');
  }, [setCurrentPage, setSoundEnabled]);

  const handlePresetChange = (presetKey: keyof typeof FLUID_PRESETS) => {
    if (presetAnimationRef.current !== null) {
      window.cancelAnimationFrame(presetAnimationRef.current);
      presetAnimationRef.current = null;
    }

    setSelectedPreset(presetKey);
    setIsPresetTransitioning(true);
    setPresetTransitionProgress(0);
    const target = FLUID_PRESETS[presetKey];
    const from = fluidSettings;
    const startAt = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startAt;
      const rawProgress = Math.min(elapsed / PRESET_TRANSITION_MS, 1);
      const p = easeInOutCubic(rawProgress);
      setPresetTransitionProgress(p);

      setFluidSettings({
        mouseForce: lerp(from.mouseForce, target.mouseForce, p),
        cursorSize: lerp(from.cursorSize, target.cursorSize, p),
        isViscous: target.isViscous,
        viscous: lerp(from.viscous, target.viscous, p),
        iterationsViscous: Math.round(lerp(from.iterationsViscous, target.iterationsViscous, p)),
        iterationsPoisson: Math.round(lerp(from.iterationsPoisson, target.iterationsPoisson, p)),
        dt: lerp(from.dt, target.dt, p),
        velocityDissipation: lerp(from.velocityDissipation, target.velocityDissipation, p),
        dyeDissipation: lerp(from.dyeDissipation, target.dyeDissipation, p),
      });

      if (rawProgress < 1) {
        presetAnimationRef.current = window.requestAnimationFrame(animate);
      } else {
        presetAnimationRef.current = null;
        setIsPresetTransitioning(false);
        setPresetTransitionProgress(0);
      }
    };

    presetAnimationRef.current = window.requestAnimationFrame(animate);
  };

  useEffect(() => () => {
    if (presetAnimationRef.current !== null) {
      window.cancelAnimationFrame(presetAnimationRef.current);
    }
  }, []);

  const updateSetting = <K extends keyof FluidSettings>(key: K, value: FluidSettings[K]) => {
    setSelectedPreset('balanced');
    setFluidSettings(prev => ({ ...prev, [key]: value }));
  };

  if (currentPage !== 'loading') {
    return null;
  }

  return (
    <div className="fixed inset-0 touch-none bg-[#f7f7f7] flex flex-col items-center justify-center p-4 sm:p-8">
      <InteractiveFluidBackground
        settings={fluidSettings}
        presetTransitionProgress={presetTransitionProgress}
      />
      <div className="absolute left-4 top-4 sm:left-8 sm:top-8">
        <Title />
      </div>
      <div className="absolute right-4 top-5 z-20 sm:right-8 sm:top-8">
        <motion.button
          type="button"
          onClick={() => setShowFluidControls(prev => !prev)}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.98 }}
          className="label inline-flex min-h-[38px] items-center gap-2 rounded-full border border-black/[0.1] bg-white/[0.68] px-3 py-1.5 text-black/60 backdrop-blur-md transition-all duration-200 hover:border-black/[0.2] hover:bg-white/[0.9] hover:text-black sm:min-h-[40px] sm:rounded-xl sm:px-3.5 sm:py-2"
        >
          <SlidersHorizontal size={14} className="opacity-75" />
          <span className="sm:hidden">{showFluidControls ? 'Close' : 'Tune'}</span>
          <span className="hidden sm:inline">{showFluidControls ? 'Close Controls' : 'Fluid Controls'}</span>
        </motion.button>
      </div>
      <AnimatePresence>
        {showFluidControls && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-3 bottom-3 z-20 max-h-[62dvh] overflow-y-auto rounded-2xl border border-black/[0.1] bg-white/[0.84] p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_10px_35px_rgba(0,0,0,0.08)] backdrop-blur-xl sm:inset-x-auto sm:bottom-auto sm:right-8 sm:top-20 sm:max-h-none sm:w-[340px] sm:overflow-visible sm:pb-4"
          >
            <motion.div variants={panelContentVariants} initial="hidden" animate="visible">
              <motion.div variants={panelItemVariants} className="mb-3">
              <label className="label mb-1 block">Preset</label>
              <div className="relative">
                <select
                  className="body w-full appearance-none rounded-lg border border-black/[0.14] bg-white/90 px-3 py-2 pr-10 text-black/75 outline-none transition-colors duration-200 hover:border-black/[0.24] focus:border-black/30"
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value as keyof typeof FLUID_PRESETS)}
                >
                  {Object.keys(FLUID_PRESETS).map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-black/45"
                  aria-hidden="true"
                />
              </div>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/[0.06]">
                <motion.div
                  className="h-full rounded-full bg-black/40"
                  animate={{ width: `${presetTransitionProgress * 100}%`, opacity: isPresetTransitioning ? 1 : 0 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  style={{ width: 0 }}
                />
              </div>
              </motion.div>
            <motion.div className="space-y-3">
              <motion.label
                variants={panelItemVariants}
                animate={isPresetTransitioning ? { backgroundColor: 'rgba(0,0,0,0.04)' } : { backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.22 }}
                className="body block rounded-lg px-2 py-1 text-black/70"
              >
                Mouse force: {fluidSettings.mouseForce.toFixed(0)}
                <input type="range" min="10" max="40" step="1" value={fluidSettings.mouseForce} onChange={(e) => updateSetting('mouseForce', Number(e.target.value))} className="mt-1 w-full accent-black/80" />
              </motion.label>
              <motion.label
                variants={panelItemVariants}
                animate={isPresetTransitioning ? { backgroundColor: 'rgba(0,0,0,0.04)' } : { backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.22, delay: 0.03 }}
                className="body block rounded-lg px-2 py-1 text-black/70"
              >
                Cursor size: {fluidSettings.cursorSize.toFixed(0)}
                <input type="range" min="30" max="90" step="1" value={fluidSettings.cursorSize} onChange={(e) => updateSetting('cursorSize', Number(e.target.value))} className="mt-1 w-full accent-black/80" />
              </motion.label>
              <motion.label
                variants={panelItemVariants}
                animate={isPresetTransitioning ? { backgroundColor: 'rgba(0,0,0,0.04)' } : { backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.22, delay: 0.06 }}
                className="body block rounded-lg px-2 py-1 text-black/70"
              >
                Viscous: {fluidSettings.viscous.toFixed(0)}
                <input type="range" min="10" max="50" step="1" value={fluidSettings.viscous} onChange={(e) => updateSetting('viscous', Number(e.target.value))} className="mt-1 w-full accent-black/80" />
              </motion.label>
              <motion.label
                variants={panelItemVariants}
                animate={isPresetTransitioning ? { backgroundColor: 'rgba(0,0,0,0.04)' } : { backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.22, delay: 0.09 }}
                className="body block rounded-lg px-2 py-1 text-black/70"
              >
                Propagation speed: {fluidSettings.dt.toFixed(3)}
                <input type="range" min="0.006" max="0.02" step="0.001" value={fluidSettings.dt} onChange={(e) => updateSetting('dt', Number(e.target.value))} className="mt-1 w-full accent-black/80" />
              </motion.label>
              <motion.label
                variants={panelItemVariants}
                animate={isPresetTransitioning ? { backgroundColor: 'rgba(0,0,0,0.04)' } : { backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.22, delay: 0.12 }}
                className="body block rounded-lg px-2 py-1 text-black/70"
              >
                Fade speed: {fluidSettings.dyeDissipation.toFixed(3)}
                <input type="range" min="0.97" max="0.999" step="0.001" value={fluidSettings.dyeDissipation} onChange={(e) => updateSetting('dyeDissipation', Number(e.target.value))} className="mt-1 w-full accent-black/80" />
              </motion.label>
            </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="relative z-10 w-full max-w-2xl text-black/70">
        {isLoaded && showEnterButton && !audioInitialized ? (
          <div className="animate-fade-in">
            <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 rounded-2xl border border-black/[0.08] bg-white/[0.68] px-5 py-6 text-center backdrop-blur-md sm:px-8 sm:py-7">
              <span className="label">Audio Experience</span>
              <div className="space-y-1">
                <p className="body text-balance text-black/78">
                  Spatial ambient sound enhances movement and depth.
                </p>
                <p className="body text-sm leading-relaxed text-black/55">
                  You can toggle audio anytime from the header.
                </p>
              </div>
              <div className="grid w-full max-w-md grid-cols-1 gap-2 sm:grid-cols-2">
              <motion.button
                type="button"
                onClick={handleEnterWithSound}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.99 }}
                className="body min-h-[38px] rounded-xl border border-black/[0.16] bg-white/[0.78] px-3.5 py-1.5 text-[0.95rem] text-black/85 transition-all duration-200 hover:border-black/[0.28] hover:bg-white hover:text-black"
              >
                Continue with sound
              </motion.button>
              <motion.button
                type="button"
                onClick={handleEnterWithoutSound}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.99 }}
                className="body min-h-[38px] rounded-xl border border-black/[0.1] bg-transparent px-3.5 py-1.5 text-[0.95rem] text-black/62 transition-all duration-200 hover:border-black/[0.2] hover:bg-black/[0.02] hover:text-black/78"
              >
                Continue without sound
              </motion.button>
              </div>
            </div>
          </div>
        ) : (
          <span className="body block w-full text-center text-black/60 animate-fade-in">
            Portfolio in development – Updates coming soon
          </span>
        )}
      </div>
      <div className="absolute bottom-8 right-8 z-10 text-6xl [font-variant-numeric:tabular-nums] text-black">
        {Math.round(loadingProgress)}%
      </div>
    </div>
  );
};
